import React from 'react';
import Sidebar from '../../../shared/components/layout/Sidebar';
import Cookies from 'js-cookie';
import Accounts from '../../../shared/assets/svg/accountsvg.svg';
import logo from '../../../shared/assets/svg/group.svg';
import liveFeed from '../../../shared/assets/png/liveFeed.svg';
import users from '../../../shared/assets/svg/users.svg';
import workforceIcon from '../../../shared/assets/svg/workforce.svg';
import legalIcon from '../../../shared/assets/svg/legal.svg';
import analytics from '../../../shared/assets/svg/analytics.svg';
import sales from '../../../shared/assets/svg/sales.svg';
import orgaisation from '../../../shared/assets/svg/organisation.svg';
import operations from '../../../shared/assets/svg/operations.svg';
import Reports from '../../../shared/assets/svg/reports.svg';
import Integration from '../../../shared/assets/svg/integration.svg';
import Logout from '../../../shared/assets/svg/logout.svg';
import SalesFlow from '../../../shared/assets/svg/SalesFlow.svg';
import Dashboard from '../../../shared/assets/svg/dashboardIcon.svg';
import inventory from '../../../shared/assets/svg/Inventory.svg';
import { Toaster } from 'react-hot-toast';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import { useBuilderWhatsappIntegration } from '../../hooks/useBuilderWhatsappIntegration';
import { useQuery } from '@tanstack/react-query';
import { getUserById, getUserActivityAccess } from '../../services/api/implementation/impl';
import { syncSessionProfileFromUser } from '../../../shared/utils/authStorage';
import { workforceNavFor } from '../../../shared/utils/workforceAccess';
import { useApproverAccess } from '../../hooks/useApproverAccess';
import { siteWalletRoleFor, useMySiteWallet } from '../../hooks/useSiteWalletAccess';
import AccessLockNotice from './AccessLockNotice';

const Layout = ({ children }) => {
  const userId = Cookies.get('userId');
  const { collapsed, setCollapsed } = useSidebar();

  // Time on System can be confined to named accounts on the server
  // (USER_ACTIVITY_VIEWERS), so ask it rather than guess from role or vertical.
  const { data: timeOnSystemAccess } = useQuery({
    queryKey: ['userActivityAccess', userId],
    queryFn: getUserActivityAccess,
    enabled: !!userId,
    retry: false,
    staleTime: 30 * 60 * 1000
  });
  const canSeeTimeOnSystem = !!timeOnSystemAccess?.allowed;

  // The account owner and the people he has assigned — decided by the
  // server, in any vertical — need a way into Workforce → Approvals.
  const { isApprover, isOwner, loaded: approverAccessLoaded } = useApproverAccess();

  const adminLinks = [
    { name: 'Dashboard', path: '/builder/dashboard', icon: Dashboard },
    {
      name: 'Management',
      path: '/builder/management',
      icon: orgaisation,
      children: [
        { displayName: 'Company', path: 'company' },
        { displayName: 'Land', path: 'land' },
        { displayName: 'Partner', path: 'partner' },
        { displayName: 'Time on System', path: 'time-on-system' },
        { displayName: 'Universal Measurement', path: 'universal-measurement' }
      ]
    },
    {
      name: 'Sales',
      path: '/builder/sales',
      icon: sales,
      children: [
        // { displayName: 'Leads', path: 'sales' },
        { displayName: 'Performance', path: 'performance' },
        { displayName: 'Deals', path: 'deals' },
        // { displayName: 'Targets & Incentives', path: 'targets-incentives' },
        // { displayName: 'Property Templates', path: 'propertyTemplates' },
        // { displayName: 'Unit Inventory', path: 'unit-inventory' },
        { displayName: 'Images', path: 'project-images' }
      ]
    },
    {
      name: 'Operations',
      path: '/builder/operations',
      icon: operations,
      children: [
        { displayName: 'Today', path: 'today' },
        { displayName: 'BOQ & BOM', path: 'boq-bom' },
        { displayName: 'Task Management', path: 'task-management' },
        { displayName: 'Create Tasks', path: 'tasks' },
        { displayName: 'Task Updates', path: 'tasksUpdates' },
        // { displayName: 'Accommodation & Blocks', path: 'accommodation-blocks' },
        // { displayName: 'Configure Holidays', path: 'configureHoliday' },
        { displayName: 'Service Providers', path: 'service-providers' },
        // { displayName: 'Change Requests', path: 'change-requests' }
      ]
    },
    {
      name: 'Workforce',
      path: '/builder/workforce',
      icon: workforceIcon,
      children: workforceNavFor(null, 'management')
    },
    { name: 'Legal', path: '/builder/legal', icon: legalIcon },
    {
      name: 'Inventory',
      path: '/builder/inventory',
      icon: inventory,
      children: [
        { displayName: 'Pipeline', path: 'pipeline' },
        { displayName: 'Stock', path: 'stock' },
        { displayName: 'Warehouses', path: 'warehouses' },
        { displayName: 'Transfers & Lends', path: 'transfers' },
        { displayName: 'Material Cost', path: 'material-cost' },
        { displayName: 'Vendors', path: 'vendors' },
        { displayName: 'Orders', path: 'orders' },
        { displayName: 'Machinery/Equipment', path: 'machinery' },
        { displayName: 'Materials', path: 'materials' },
        { displayName: 'Requirements', path: 'requirements' },
        { displayName: 'Monthly Report', path: 'monthly-report' },
        { displayName: 'Consumption Report', path: 'consumption-report' },
        { displayName: 'Escalation Report', path: 'escalation-report' }
      ]
    },

    {
      name: 'Marketing',
      path: '/builder/marketing',
      icon: SalesFlow,
      children: [
        { displayName: 'Dashboard', path: 'dashboard' },
        { displayName: 'Campaign/Forms', path: 'forms' },
        { displayName: 'Create Campaign', path: `add/campaign` },
        { displayName: 'Templates', path: `templates` },
        // WhatsApp Campaigns is injected in useMemo when integration is configured
        { displayName: 'Prelead List', path: 'prelead-lists' }
      ]
    },

    {
      name: 'Accounts',
      path: '/builder/accounts',
      icon: Accounts,
      children: [
        { displayName: 'Invoice', path: 'invoice' },
        { displayName: 'Payables', path: 'payables' },
        { displayName: 'Payables History', path: 'history' },
        { displayName: 'Priorities', path: 'priorities' },
        { displayName: 'Payment Stages', path: 'payment-stages' }
      ]
    },

    { name: 'Users', path: '/builder/users', icon: users },

    {
      name: 'Integration',
      path: '/builder/integration',
      icon: Integration
    },
    {
      name: 'Reports',
      path: '/builder/reports',
      icon: Reports,
      children: [
        { displayName: 'Sales Report', path: 'sales' },
        { displayName: 'Accounts Report', path: 'accounts' },
        // The Workforce reports, split the way the admin asks for them.
        { displayName: 'Employee Reports', path: '/builder/workforce/reports?kind=employees' },
        { displayName: 'Labour Reports', path: '/builder/workforce/reports?kind=labour' }
      ]
    },
    { name: 'Live Feed', path: '/builder/feed', icon: liveFeed },
    { name: 'My Payslips', path: '/builder/my-payslips', icon: users },
    { name: 'My Policies', path: '/builder/my-policies', icon: users },
    { name: 'My Leave', path: '/builder/my-leave', icon: users },
    { name: 'Logout', path: 'logout', icon: Logout }
  ];

  const readSessionFromCookies = React.useCallback(
    () => ({
      userRole: Cookies.get('userRole'),
      userVertical: Cookies.get('userVertical'),
      userDesignationLevel: Cookies.get('userDesignationLevel'),
      // The job title, not the seniority band — HR Manager and HR Executive
      // share a vertical and a level, and only the title separates them.
      userDesignationName: Cookies.get('userDesignation')
    }),
    []
  );

  const [sessionMeta, setSessionMeta] = React.useState(readSessionFromCookies);

  React.useEffect(() => {
    const syncSessionMeta = () => setSessionMeta(readSessionFromCookies());
    window.addEventListener('userLoggedIn', syncSessionMeta);
    return () => window.removeEventListener('userLoggedIn', syncSessionMeta);
  }, [readSessionFromCookies]);

  React.useEffect(() => {
    let cancelled = false;
    const RECONCILE_TTL_MS = 5 * 60 * 1000;
    const storageKey = `sessionProfileReconciledAt:${userId || 'anon'}`;

    const reconcileSessionProfile = async (force = false) => {
      const id = Cookies.get('userId');
      if (!id) return;

      if (!force) {
        const last = Number(sessionStorage.getItem(storageKey) || 0);
        if (last && Date.now() - last < RECONCILE_TTL_MS) {
          return;
        }
      }

      try {
        const user = await getUserById(id);
        if (cancelled || !user) return;
        sessionStorage.setItem(storageKey, String(Date.now()));

        if (syncSessionProfileFromUser(user)) {
          setSessionMeta(readSessionFromCookies());
          window.dispatchEvent(new CustomEvent('sessionProfileSynced'));
        }
      } catch (error) {
        console.error('Failed to reconcile session profile:', error);
      }
    };

    reconcileSessionProfile(false);
    const onLogin = () => reconcileSessionProfile(true);
    window.addEventListener('userLoggedIn', onLogin);

    return () => {
      cancelled = true;
      window.removeEventListener('userLoggedIn', onLogin);
    };
  }, [readSessionFromCookies, userId]);

  const { userRole, userVertical, userDesignationLevel, userDesignationName } =
    sessionMeta;
  const { allowWhatsAppCampaignsSidebar } = useBuilderWhatsappIntegration();

  // Site Wallet: Accounts → Site Wallets for the builder side (owner,
  // Management, High) and Accounts; Operations → My Wallet for an Operations
  // Manager or Executive, and only once GET /siteWallet/me says they have one.
  const siteWalletRole = siteWalletRoleFor({
    vertical: userVertical,
    level: userDesignationLevel,
    isOwner
  });
  const mySiteWallet = useMySiteWallet(siteWalletRole.mayHoldWallet);
  const hasSiteWallet = siteWalletRole.mayHoldWallet && Boolean(mySiteWallet.data);
  const showSiteWallets = siteWalletRole.isStaff;

  const verticalLinks = React.useMemo(() => {
    const vertical = userVertical?.toLowerCase();
    const designation = userDesignationLevel?.toLowerCase();

    const salesChildren = [
      // ...(vertical === 'management' ? [{ displayName: 'Dashboard', path: 'dashboard' }] : []),
      // { displayName: 'Leads', path: 'sales' },
      ...(designation === 'medium' || designation === 'high'
        ? [
            ...(vertical !== 'management'
              ? [{ displayName: 'Performance', path: 'performance' }]
              : [])
            // { displayName: 'Targets & Incentives', path: 'targets-incentives' },
            // { displayName: 'Property Templates', path: 'propertyTemplates' }
          ]
        : []),
      // { displayName: 'Unit Inventory', path: 'unit-inventory' },
      { displayName: 'Deals', path: 'deals' },
      { displayName: 'Images', path: 'project-images' }
    ];

    const operationsChildren = [
      { displayName: 'Today', path: 'today' },
      { displayName: 'BOQ & BOM', path: 'boq-bom' },
      { displayName: 'Task Management', path: 'task-management' },
      { displayName: 'Create Tasks', path: 'tasks' },
      { displayName: 'Task Updates', path: 'tasksUpdates' },
      // { displayName: 'Accommodation & Blocks', path: 'accommodation-blocks' },
      // Task templates live under Task Management → Task Template tab
      // { displayName: 'Configure Holidays', path: 'configureHoliday' },
      { displayName: 'Service Providers', path: 'service-providers' },
      // { displayName: 'Change Requests', path: 'change-requests' }
      ...(hasSiteWallet ? [{ displayName: 'My Wallet', path: 'my-wallet' }] : [])
    ];

    const inventoryChildren = [
      { displayName: 'Pipeline', path: 'pipeline' },
      { displayName: 'Stock', path: 'stock' },
      { displayName: 'Warehouses', path: 'warehouses' },
      { displayName: 'Transfers & Lends', path: 'transfers' },
      { displayName: 'Material Cost', path: 'material-cost' },
      { displayName: 'Vendors', path: 'vendors' },
      { displayName: 'Orders', path: 'orders' },
      { displayName: 'Machinery/Equipment', path: 'machinery' },
      { displayName: 'Materials', path: 'materials' },
      { displayName: 'Requirements', path: 'requirements' },
      { displayName: 'Monthly Report', path: 'monthly-report' },
      { displayName: 'Consumption Report', path: 'consumption-report' },
      { displayName: 'Escalation Report', path: 'escalation-report' }
    ];

    const accountsChildren = [
      { displayName: 'Invoice', path: 'invoice' },
      { displayName: 'Payables', path: 'payables' },
      { displayName: 'Payables History', path: 'history' },
      { displayName: 'Priorities', path: 'priorities' },
      ...(vertical === 'accounts'
        ? [
            { displayName: 'Payment Stages', path: 'payment-stages' },
            { displayName: 'Management Tasks', path: 'managementTasks' }
          ]
        : []),
      ...(showSiteWallets ? [{ displayName: 'Site Wallets', path: 'site-wallets' }] : [])
    ];

    const marketingChildren = [
      { displayName: 'Dashboard', path: 'dashboard' },
      { displayName: 'Campaign/Forms', path: 'forms' },
      { displayName: 'Create Campaign', path: 'add/campaign' },
      { displayName: 'Templates', path: 'templates' },
      ...(allowWhatsAppCampaignsSidebar
        ? [
            { displayName: 'WhatsApp Campaigns', path: 'whatsapp-campaigns' },
            { displayName: 'WhatsApp Flow', path: 'whatsapp-flow' }
          ]
        : []),
      ...(vertical !== 'management' ? [{ displayName: 'Prelead List', path: 'prelead-lists' }] : [])
    ];

    /**
     * The sidebar offers the Workforce dashboard and the muster, and nothing
     * else — everything is grouped by track on the dashboard instead. Role
     * filtering lives in workforceNavFor, so somebody who cannot see the
     * muster does not get it here either.
     */
    const workforceChildren = workforceNavFor(userDesignationName, userVertical);
    const firstWorkforcePath =
      workforceChildren.find((item) => item.path)?.path || '/builder/workforce';

    const modifiedAdminLinks = adminLinks.map((link) => {
      if (link.name === 'Workforce') {
        return {
          ...link,
          path: firstWorkforcePath,
          children: workforceChildren
        };
      }
      if (link.name === 'Management' && !canSeeTimeOnSystem) {
        return {
          ...link,
          children: link.children.filter((child) => child.path !== 'time-on-system')
        };
      }
      if (link.name === 'Sales') {
        return { ...link, children: salesChildren };
      }
      if (link.name === 'Operations') {
        return { ...link, children: operationsChildren };
      }
      if (link.name === 'Inventory') {
        return { ...link, children: inventoryChildren };
      }
      if (link.name === 'Accounts') {
        return { ...link, children: accountsChildren };
      }
      if (link.name === 'Marketing') {
        return { ...link, children: marketingChildren };
      }
      return link;
    });

    if (
      userRole === 'builder' &&
      vertical === 'sales' &&
      (designation === 'medium' || designation === 'high')
    ) {
      return modifiedAdminLinks.filter(
        (link) =>
          // link.name === 'Dashboard' ||
          link.name === 'Sales' || link.name === 'Live Feed' || link.name === 'Logout'
        // Users hidden for non-management verticals
        // || link.name === 'Users'
      );
    }

    switch (vertical) {
      case 'operations':
        return modifiedAdminLinks.filter(
          (link) =>
            link.name === 'Dashboard' ||
            link.name === 'Operations' ||
            link.name === 'Inventory' ||
            link.name === 'Workforce' ||
            link.name === 'Live Feed' ||
            link.name === 'Logout'
          // Users hidden for non-management verticals
          // || link.name === 'Users'
        );
      case 'sales':
        // Only LOW designation reaches here (medium/high handled above)
        return modifiedAdminLinks.filter(
          (link) =>
            // link.name === 'Dashboard' ||
            link.name === 'Sales' || link.name === 'Live Feed' || link.name === 'Logout'
          // Users hidden for LOW designation
        );
      case 'accounts':
        return modifiedAdminLinks.filter(
          (link) =>
            link.name === 'Reports' ||
            link.name === 'Accounts' ||
            link.name === 'Live Feed' ||
            link.name === 'Logout'
        );
      case 'marketing':
        return modifiedAdminLinks.filter(
          (link) =>
            link.name === 'Marketing' ||
            link.name === 'Live Feed' ||
            link.name === 'Integration' ||
            link.name === 'Logout'
        );
      case 'inventory':
        return modifiedAdminLinks.filter(
          (link) =>
            link.name === 'Dashboard' ||
            link.name === 'Inventory' ||
            link.name === 'Live Feed' ||
            link.name === 'Logout'
        );
      case 'hr':
        return modifiedAdminLinks.filter(
          (link) =>
            link.name === 'Workforce' ||
            link.name === 'Live Feed' ||
            link.name === 'Logout'
        );
      case 'staff':
        // Onboarded by HR, no module of their own: payslips, policies and
        // leave are added back below as self-service, so only Logout here.
        return modifiedAdminLinks.filter((link) => link.name === 'Logout');
      case 'legal':
        // Legal works the queue and the deals in it. Sales is on the list
        // because a reviewer opens the deal from the lead it belongs to —
        // the API still scopes what they can do there.
        return modifiedAdminLinks.filter(
          (link) =>
            link.name === 'Legal' ||
            link.name === 'Sales' ||
            link.name === 'Live Feed' ||
            link.name === 'Logout'
        );
      case 'management':
        // All management vertical users see the full builder sidebar (API still enforces permissions).
        return modifiedAdminLinks;
      default:
        return modifiedAdminLinks;
    }
  }, [
    userRole,
    userVertical,
    userDesignationLevel,
    userDesignationName,
    allowWhatsAppCampaignsSidebar,
    canSeeTimeOnSystem,
    hasSiteWallet,
    showSiteWallets
  ]);

  // Only the main builder account (Management + High) runs payroll and does
  // not collect a slip. Other Management people are paid staff and still see
  // My Payslips.
  const filteredLinks = React.useMemo(() => {
    // The server says who the owner is (GET /approvals/me). Until it answers,
    // keep the old guess: Management at High level. A Management Admin who is
    // not the owner is staff, and gets their own payslips, leave and policies.
    const isMainBuilderAccount = approverAccessLoaded
      ? isOwner
      : String(userRole || '').toLowerCase() === 'builder' &&
        String(userVertical || '').toLowerCase() === 'management' &&
        userDesignationLevel === 'High';
    // Every employee's own pages: their payslips, leave and the policies they
    // are asked to read and accept.
    const SELF_SERVICE = ['My Payslips', 'My Policies', 'My Leave'];
    const withoutSelf = verticalLinks.filter((link) => !SELF_SERVICE.includes(link.name));
    // Approvals is a tab inside Workforce. An approver the owner picked from
    // Sales or Accounts has no Workforce entry of his own, so he gets one that
    // opens straight on Approvals.
    const withApprovals = (links) => {
      if (!isApprover) return links;
      if (links.some((link) => link.name === 'Workforce')) return links;
      const workforceLink = {
        name: 'Workforce',
        path: '/builder/workforce?tab=approvals',
        icon: workforceIcon
      };
      const logoutAt = links.findIndex((link) => link.name === 'Logout');
      if (logoutAt === -1) return [...links, workforceLink];
      return [...links.slice(0, logoutAt), workforceLink, ...links.slice(logoutAt)];
    };
    // A High user outside Management and Accounts (Sales, Operations…) is
    // builder side for Site Wallets but has no Accounts entry; give them one
    // holding Site Wallets and the wallet approvals on Priorities.
    const withSiteWallets = (links) => {
      if (!showSiteWallets || links.some((link) => link.name === 'Accounts')) return links;
      const accountsLink = {
        name: 'Accounts',
        path: '/builder/accounts/site-wallets',
        icon: Accounts,
        children: [
          { displayName: 'Site Wallets', path: '/builder/accounts/site-wallets' },
          { displayName: 'Wallet Approvals', path: '/builder/accounts/priorities?tab=wallet-approvals' }
        ]
      };
      const logoutAt = links.findIndex((link) => link.name === 'Logout');
      if (logoutAt === -1) return [...links, accountsLink];
      return [...links.slice(0, logoutAt), accountsLink, ...links.slice(logoutAt)];
    };
    if (isMainBuilderAccount) return withApprovals(withSiteWallets(withoutSelf));

    const selfLinks = SELF_SERVICE.map(
      (name) =>
        verticalLinks.find((link) => link.name === name) ??
        adminLinks.find((link) => link.name === name)
    ).filter(Boolean);
    if (!selfLinks.length) return withApprovals(withSiteWallets(withoutSelf));

    const logoutAt = withoutSelf.findIndex((link) => link.name === 'Logout');
    if (logoutAt === -1) return withApprovals(withSiteWallets([...withoutSelf, ...selfLinks]));
    return withApprovals(
      withSiteWallets([
        ...withoutSelf.slice(0, logoutAt),
        ...selfLinks,
        ...withoutSelf.slice(logoutAt)
      ])
    );
  }, [userDesignationLevel, userRole, userVertical, verticalLinks, isApprover, showSiteWallets, isOwner, approverAccessLoaded]);

  return (
    <div className="flex justify-between items-stretch relative min-h-screen">
      {/* Sidebar */}
      <Sidebar
        role="builder"
        links={filteredLinks}
        logo={logo}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />

      {/* Main content outlet */}
      <div
        className={`flex-1 bg-gray-100 min-h-screen absolute top-0
          transition-all duration-300 ease-in-out
          ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
          md:${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
        `}
      >
        <AccessLockNotice>{children}</AccessLockNotice>
      </div>

      <Toaster position="top-right" />
    </div>
  );
};

export default Layout;
